import { createSlice, Slice } from '@reduxjs/toolkit'

import { DeserializedSavedFilter } from 'api'
import { DateRange } from 'components/DateRangePicker'
import { FilterGroup, FilterGroupsByPage, FilterPage } from 'utils/filters'
import { removeFilter as removeFilterUtil } from 'utils/filters'

export interface FiltersState {
  loaded: boolean
  defaultFilterId: number | undefined
  savedFilters: DeserializedSavedFilter[]
  dateRange?: DateRange
  filterGroupsByPage: FilterGroupsByPage
  searchQuery?: string
  activeFilterId?: number
}

export const initialState: FiltersState = {
  loaded: false,
  defaultFilterId: undefined,
  savedFilters: [],
  filterGroupsByPage: {
    ACCOUNTS: [[]],
    ACTIVITIES: [[]]
  },
  dateRange: undefined,
  searchQuery: undefined,
  activeFilterId: undefined
}

const isValidPage = (page: FilterPage) => FilterPage[page] !== undefined

export const filtersSlice: Slice<FiltersState> = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSavedFilters: (
      state: FiltersState,
      { payload }: { payload: DeserializedSavedFilter[] }
    ) => ({
      ...state,
      loaded: true,
      savedFilters: payload
    }),
    setDefaultFilterId: (state, { payload }) => ({
      ...state,
      defaultFilterId: payload
    }),
    setActiveFilterId: (state, { payload }) => ({
      ...state,
      activeFilterId: payload
    }),
    setFilterGroupsByPage: (state, { payload }) => ({
      ...state,
      filterGroupsByPage: payload
    }),
    changeFilterGroups: (
      state: FiltersState,
      {
        payload: { page, filterGroups }
      }: { payload: { page: FilterPage; filterGroups: FilterGroup[] } }
    ) => {
      if (!isValidPage(page)) {
        return state
      }

      return {
        ...state,
        filterGroupsByPage: { ...state.filterGroupsByPage, [page]: filterGroups }
      }
    },
    removeFilter: (
      state: FiltersState,
      {
        payload: { page, groupIndex, filterIndex }
      }: { payload: { page: FilterPage; groupIndex: number; filterIndex: number } }
    ) => {
      const groups = state.filterGroupsByPage[page]
      const nextGroups = removeFilterUtil(groups, groupIndex, filterIndex)

      if (!isValidPage(page)) {
        return state
      }

      return {
        ...state,
        filterGroupsByPage: { ...state.filterGroupsByPage, [page]: nextGroups }
      }
    },
    clearFilters: (
      state: FiltersState,
      { payload: { page } }: { payload: { page: FilterPage } }
    ) => ({
      ...state,
      filterGroupsByPage: {
        ...state.filterGroupsByPage,
        [page]: initialState.filterGroupsByPage[page]
      }
    }),
    setDateRange: (state: FiltersState, { payload }: { payload: DateRange | undefined }) => ({
      ...state,
      dateRange: payload
    }),
    setSearchQuery: (state: FiltersState, { payload }: { payload: string }) => ({
      ...state,
      searchQuery: payload
    })
  }
})

const { reducer: FiltersReducer, actions } = filtersSlice

export const {
  setSavedFilters,
  setDefaultFilterId,
  setFilterGroupsByPage,
  setDateRange,
  changeFilterGroups,
  clearFilters,
  removeFilter,
  setSearchQuery,
  setActiveFilterId
} = actions

export default FiltersReducer
